import { getPlatformUrlParts } from '@acl-services/highbond-common';
import {
  detectPlatformEnvKind,
  isComplianceEducationNavTreeEnabled,
  isContentAuthoringEnabled,
  isCourseLibrarySearchEnabled,
  isInternalDevToolsEnabled,
  isMalwareScanningEnabled,
  isSalesOpsAiRecommendationEnabled,
  isUserSettingsEnabled,
} from './utils/envFeatureFlags';

const {
  protocol,
  appSubdomain,
  domain,
  region,
  domainSuffix,
  tld,
  isDev,
  isLocal,
  isStaging,
  isPreprod,
  isProduction,
  isGovCloud,
} = getPlatformUrlParts(window.location.origin, true);

const host = isLocal ? 'localhost' : `${domain}${domainSuffix}.${tld}`;

/**
 * DR 2026 UK drill: `*-uk` hostnames parse as region `uk`. CE is US-regional (oidc-us in US)
 * so OIDC fails over to `oidc-us-uk`; identity/login uses global `accounts-uk` in eu-west-2.
 */
const isUkDrPlayground =
  !isLocal && isDev && region === 'uk' && host === 'diligentoneplatform-dev.com';

const accountsOrigin = isUkDrPlayground
  ? `${protocol}://accounts-uk.${host}`
  : `${protocol}://accounts.${host}`;

/**
 * True when running via Vite dev/preview on a local port (proxy available).
 *
 * `import.meta.env.DEV` covers `npm run dev` on any port — Vite auto-increments past a
 * busy 3010, so a port allowlist would be wrong there. The loopback check is for
 * `vite preview`, which serves a production build (DEV false) but still installs the
 * same proxies (`preview.proxy` in vite.config.ts), and likewise auto-increments past a
 * busy 3000. Deliberately not keyed off `isLocal`: that is also true for any http://
 * origin, including a deployed one, where no proxy exists to serve the path.
 */
function useLocalApiProxy(): boolean {
  const { hostname } = window.location;
  const isLoopbackHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]';
  return import.meta.env.DEV || isLoopbackHost;
}

const useApiProxy = useLocalApiProxy();

// Make appSubdomain and isLocal globally accessible for access control checks
// This allows UserProfile functions to check subdomain without circular dependencies
// Type definitions are in src/global.d.ts
window.__APP_SUBDOMAIN__ = appSubdomain;
window.__IS_LOCAL__ = isLocal;
console.log('[AppConfig] Subdomain extracted from URL:', appSubdomain, '(isLocal:', isLocal, ')');

// Construct OIDC URL dynamically - use diligentoneplatform-dev for local, platform OIDC for deployed
const oidcUrl = isLocal
  ? (import.meta.env.VITE_OIDC_URL || 'https://oidc-us.diligentoneplatform-dev.com')
  : isUkDrPlayground
    ? `${protocol}://oidc-us-uk.${host}`
    : `${protocol}://oidc-${region}.${host}`;

// Construct Compliance Education API URL dynamically
// For local development, use dev API; for deployed environments, use platform API
// EU and other non-US regions use a region-suffixed subdomain (e.g. compliance-education-api-eu.*)
const apiRegionSuffix = region !== 'us' ? `-${region}` : '';

// Local Vite: same-origin proxy paths (see vite.config.ts). VITE_* env vars are proxy targets only.
const complianceEducationApiUrl = useApiProxy
  ? 'https://compliance-education-api.diligentoneplatform-dev.com'
  : `${protocol}://compliance-education-api${apiRegionSuffix}.${host}`;

// Content Authoring API (CEE-829) — its own gateway/domain, separate from the portal API.
// Local dev uses the same-origin Vite proxy path (see createDevApiProxies in
// vite.config.ts) rather than the absolute dev URL: the authoring API's CORS allowlist
// only covers localhost:3000 and :3010 (cors-utils.ts), so a direct browser call fails on
// any other port. The proxy is server-side, so no preflight is involved and the port
// stops mattering.
// VITE_AUTHORING_API_URL retargets the proxy (e.g. at a scoped feature pipeline, whose
// subdomain is prefixed with the scope: {scope}compliance-authoring-api...).
const complianceAuthoringApiUrl = useApiProxy
  ? '/compliance-authoring-api'
  : `${protocol}://compliance-authoring-api${apiRegionSuffix}.${host}`;

  // const complianceEducationApiUrl = isLocal
  // ? (import.meta.env.VITE_API_URL || 'https://compliance-education-api.diligentoneplatform-dev.com')
  // : `${protocol}://compliance-education-api${apiRegionSuffix}.${host}`;

// Construct SCORM Preview Domain dynamically using pattern matching
// Works for both diligentoneplatform and highbond domains
// Local: defaults to dev, can be overridden via VITE_SCORM_PREVIEW_DOMAIN
// Deployed: scorm-preview.{host} for US, scorm-preview-eu.{host} for EU (matches CDK subDomainSuffix)
const scormPreviewDomain = isLocal
  ? (import.meta.env.VITE_SCORM_PREVIEW_DOMAIN || 'https://scorm-preview.diligentoneplatform-dev.com')
  : `${protocol}://scorm-preview${apiRegionSuffix}.${host}`;

// Local: platform accounts host (Settings card link); deployed: accounts.{host}/profile
const platformProfileUrl = isLocal
  ? (import.meta.env.VITE_PLATFORM_PROFILE_URL || 'https://accounts.diligentoneplatform-dev.com/profile')
  : `${accountsOrigin}/profile`;

const platformEnvKind = detectPlatformEnvKind({
  isLocal,
  isDev,
  isPreprod,
  isProduction,
});
const courseLibrarySearchEnabled = isCourseLibrarySearchEnabled(platformEnvKind);
const userSettingsEnabled = isUserSettingsEnabled(platformEnvKind);
const internalDevToolsEnabled = isInternalDevToolsEnabled(platformEnvKind);
const salesOpsAiRecommendationEnabled = isSalesOpsAiRecommendationEnabled(platformEnvKind);
const contentAuthoringEnabled = isContentAuthoringEnabled(platformEnvKind);
const complianceEducationNavTreeEnabled = isComplianceEducationNavTreeEnabled(platformEnvKind);

const malwareScanningEnabled = isMalwareScanningEnabled(platformEnvKind);

const AppConfig = {
  apiEndpoint: `${protocol}://${appSubdomain}${region}.${host}`,
  /** Diligent One Platform (accounts) — user/org management for standard users */
  platformProfileUrl,
  loginUrl: `${accountsOrigin}/login`,
  tokenRefreshUrl: `${accountsOrigin}/api/token/refresh`,
  pendoMonitorScript: `${protocol}://monitor.${host}/v1/monitor.js`,
  oidcClientUrl: oidcUrl,
  complianceEducationApiUrl,
  /** Content Authoring API (`compliance-authoring-api.*`) — templates, courses, media. */
  complianceAuthoringApiUrl,
  scormPreviewDomain,
  region,
  isLocal,
  isDev,
  isStaging,
  isPreprod,
  isProduction,
  isGovCloud,
  appSubdomain, // Export subdomain for access control checks
  courseLibrarySearchEnabled,
  userSettingsEnabled,
  /** AI Recommendation + onboarding org test controls (local and dev only). */
  internalDevToolsEnabled,
  /** Presales AI recommendation for allowlisted orgs (e.g. sales-ops). */
  salesOpsAiRecommendationEnabled,
  /** Content Authoring portal (/templates) — local and dev only. */
  contentAuthoringEnabled,
  /** Hierarchical Compliance Education nav tree — local and dev only. */
  complianceEducationNavTreeEnabled,
  malwareScanningEnabled,
  /** Marketing URL for Explore packages CTA (CEE-1025). Button hidden until this is set. */
  samplePreviewExplorePackagesUrl: import.meta.env.VITE_SAMPLE_PREVIEW_EXPLORE_PACKAGES_URL || '',
  microFrontends: {
    'hb-global-navigator': {
      url: `${protocol}://web-components.${host}/global-navigator/index.js`,
    },
  },
};

export default AppConfig;
